/**
 * 字体管理（M2-16 / DESIGN.md §2.5）。
 *
 * §2.5 的三条字体来源，这里落前两条：
 * ① 内置开源字体（思源黑体常用字子集 / Inter / JetBrains Mono），
 *    随应用离线打包（public/fonts/，许可证 OFL-1.1 同目录附带并登记在
 *    THIRD_PARTY_LICENSES.md）；
 * ② 用户拖入字体文件，解析校验后存 OPFS，跨会话可枚举。
 *
 * 内置字体用**未压缩 OTF/TTF**：HarfBuzz（排版引擎 M2-17）不解 woff2，
 * fontkit 能——两个消费方的最大公约数是 sfnt。离线应用不走网络，
 * woff2 的传输压缩优势在这里不存在。
 * ③ Local Font Access（仅 Chromium，需要用户授权）：见 `local-access.ts`，
 * 只在这一层之外单独成文件——它探测的是浏览器能力而不是解析字体字节，
 * 跟这个文件"字体元数据怎么解析/怎么存"的职责不是一回事。
 *
 * 解析用 fontkit（附录 A 选型）。**防御性解析**（CLAUDE.md 规则 10）：
 * 输入长度上限 + try/catch 包住一切——字体文件是用户可控输入，
 * 畸形字节必须优雅拒绝而不是崩掉应用。
 */
import * as fontkit from 'fontkit';
import type { WorkspaceDirLike } from '../gateway';

/** 单个字体文件的上限。CJK 全量字体约 20MB，32MB 足以容纳一切正当输入。 */
export const MAX_FONT_BYTES = 32 * 1024 * 1024;

export interface FontMetadata {
  /** 族名（如 "Inter"）。 */
  readonly family: string;
  /** 样式名（如 "Regular"、"Bold Italic"）。 */
  readonly style: string;
  /** OS/2 权重 100–900。 */
  readonly weight: number;
  readonly italic: boolean;
  readonly glyphCount: number;
  /** 支持的字符数（cmap 条目）。 */
  readonly characterCount: number;
}

export interface FontRecord extends FontMetadata {
  /** `system` = 从 Local Font Access 导入（DESIGN.md §2.5 第③条来源）。 */
  readonly source: 'builtin' | 'user' | 'system';
  /** 原始字节。排版（harfbuzz shaping）与导出子集都要它。 */
  readonly bytes: Uint8Array;
}

export class FontParseError extends Error {
  constructor(message: string) {
    super(`字体解析失败：${message}`);
    this.name = 'FontParseError';
  }
}

/**
 * 解析字体元数据。畸形输入抛 {@link FontParseError}，绝不让底层异常裸奔。
 */
export function parseFontMetadata(bytes: Uint8Array): FontMetadata {
  if (bytes.length === 0) throw new FontParseError('空文件');
  if (bytes.length > MAX_FONT_BYTES) {
    throw new FontParseError(`文件 ${bytes.length} 字节，超过 ${MAX_FONT_BYTES} 上限`);
  }
  let font: ReturnType<typeof fontkit.create>;
  try {
    font = fontkit.create(bytes as never);
  } catch (error) {
    throw new FontParseError(error instanceof Error ? error.message : String(error));
  }
  // 字体集合（ttc）取第一款
  const face = 'fonts' in font ? (font as { fonts: unknown[] }).fonts[0] : font;
  if (face == null || typeof face !== 'object') throw new FontParseError('无法读取字面');
  const f = face as {
    familyName?: string;
    subfamilyName?: string;
    numGlyphs?: number;
    characterSet?: number[];
    'OS/2'?: { usWeightClass?: number; fsSelection?: { italic?: boolean } };
    italicAngle?: number;
  };
  try {
    const weight = f['OS/2']?.usWeightClass ?? 400;
    const italic =
      f['OS/2']?.fsSelection?.italic === true || (f.italicAngle !== undefined && f.italicAngle !== 0);
    return {
      family: f.familyName ?? '未命名',
      style: f.subfamilyName ?? 'Regular',
      weight,
      italic,
      glyphCount: f.numGlyphs ?? 0,
      characterCount: f.characterSet?.length ?? 0,
    };
  } catch (error) {
    throw new FontParseError(error instanceof Error ? error.message : String(error));
  }
}

/* ------------------------------------------------------------------ */
/* 内置字体                                                             */
/* ------------------------------------------------------------------ */

export interface BuiltinFontSpec {
  readonly family: string;
  /** 相对应用根的资源路径。 */
  readonly path: string;
  readonly license: 'OFL-1.1';
}

/** 随应用打包的字体（public/fonts/）。§2.5 点名的三款。 */
export const BUILTIN_FONTS: readonly BuiltinFontSpec[] = [
  {
    // 文件名带 SC，但 fontkit 解析这个 .otf 得到的真实 familyName 是
    // "Source Han Sans CN"（`node -e "console.log(fontkit.create(...).familyName)"`
    // 验证过）——这个字段只用来标注/文档化，`loadBuiltinFonts` 实际注册的
    // `family` 来自 `parseFontMetadata(bytes)` 的解析结果，不是这里写的
    // 字符串，写错了不会让加载失败，但会跟真实注册的族名对不上，之前
    // 就是因为这里写的是 SC 而 `registry.ts` 的静态 choices 抄了这个错值，
    // 导致选中这款字体时 `ToolManager.setOption` 拿真实族名 "…CN" 去校验
    // 静态 choices 里的 "…SC"，直接抛 RangeError（state/PROGRESS.md 有记录）
    family: 'Source Han Sans CN',
    path: 'fonts/SourceHanSansSC-subset.otf',
    license: 'OFL-1.1',
  },
  { family: 'Inter', path: 'fonts/Inter-Regular.ttf', license: 'OFL-1.1' },
  { family: 'JetBrains Mono', path: 'fonts/JetBrainsMono-Regular.ttf', license: 'OFL-1.1' },
];

/** 资源加载器。浏览器 fetch；测试读文件系统。 */
export type FontAssetLoader = (path: string) => Promise<Uint8Array>;

export async function loadBuiltinFonts(load: FontAssetLoader): Promise<FontRecord[]> {
  const records: FontRecord[] = [];
  for (const spec of BUILTIN_FONTS) {
    const bytes = await load(spec.path);
    records.push({ ...parseFontMetadata(bytes), source: 'builtin', bytes });
  }
  return records;
}

/* ------------------------------------------------------------------ */
/* 用户字体（OPFS）                                                      */
/* ------------------------------------------------------------------ */

export class UserFontStore {
  constructor(private readonly dir: WorkspaceDirLike) {}

  /** OPFS 不可用时返回 null——没有用户字体不影响内置字体。 */
  static async open(
    directory = 'photosmith-fonts',
    env: {
      storage?: { getDirectory?: () => Promise<unknown> } | undefined;
    } = globalThis.navigator ?? {},
  ): Promise<UserFontStore | null> {
    const getDirectory = env.storage?.getDirectory;
    if (typeof getDirectory !== 'function') return null;
    try {
      const root = (await getDirectory.call(env.storage)) as {
        getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<unknown>;
      };
      return new UserFontStore((await root.getDirectoryHandle(directory, { create: true })) as WorkspaceDirLike);
    } catch {
      return null;
    }
  }

  /** 导入一个字体文件：先解析校验（失败即拒绝，不落盘），再持久化。 */
  async add(bytes: Uint8Array): Promise<FontRecord> {
    const metadata = parseFontMetadata(bytes); // 校验在前，垃圾不落盘
    const name = `${encodeURIComponent(`${metadata.family}-${metadata.style}`)}.font`;
    const handle = await this.dir.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    await writable.write(bytes as BufferSource);
    await writable.close();
    return { ...metadata, source: 'user', bytes };
  }

  /** 枚举已导入字体（重启后从 OPFS 重新解析）。坏文件跳过并计数，不拖垮枚举。 */
  async list(): Promise<{ fonts: FontRecord[]; skipped: number }> {
    const fonts: FontRecord[] = [];
    let skipped = 0;
    for await (const name of this.dir.keys()) {
      if (!name.endsWith('.font')) continue;
      try {
        const handle = await this.dir.getFileHandle(name);
        const bytes = new Uint8Array(await (await handle.getFile()).arrayBuffer());
        fonts.push({ ...parseFontMetadata(bytes), source: 'user', bytes });
      } catch {
        skipped++; // 单个坏文件不应让整个字体面板消失
      }
    }
    return { fonts, skipped };
  }

  async remove(family: string, style: string): Promise<void> {
    const name = `${encodeURIComponent(`${family}-${style}`)}.font`;
    try {
      await this.dir.removeEntry(name);
    } catch {
      /* 不存在即已删除 */
    }
  }
}
